/*
 * based on SingleSignInPage from backstage/packages/core-components/src/layout/SignInPage
 */
import {
  BackstageIdentityResponse,
  SignInPageProps,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import React, { ComponentType, useState } from 'react';
import { useMountEffect } from '@react-hookz/web';
import { Progress } from '@backstage/core-components';
import { SignInProviderConfig } from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useSearchParams } from 'react-router-dom';

import CaiseLogo from '../Root/CaiseLogoNegative';
import { pagesTranslationRef } from '../../translations/pages';
import LinkArrow, { LinkArrowHoverStyle } from '../LinkArrow';


const useStyles = makeStyles({
    main: {
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexFlow: 'column',
        gap: '1rem',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundImage: 'linear-gradient(180deg,rgba(13,30,47,0.38) 0%,rgba(13,30,47,0.92) 67%)',
        overflow: 'hidden',
    },
    logo: {
        marginTop: '-2rem',
        minHeight: '12rem',
    },
    signin: {
        background: 'none',
        font: 'inherit',
        cursor: 'pointer',
        outline: 'inherit',
        width: 'calc(min(100%, 34.25rem))',
        padding: '2rem',
        gap: '1rem',
        justifyContent: 'center',
        alignItems: 'stretch',
        display: 'flex',
        flexFlow: 'column',
        borderRadius: '1.25rem',
        border: '1px solid hsl(from #0cabf3 calc(h + 0) calc(s + 0) calc(l + 0) / 0.3)',
        color: 'white',
        transition: 'border-color,box-shadow 0.3s,0.3s',
        backdropFilter: 'blur(8px)',
        backgroundImage:
            'radial-gradient(' +
            'circle at bottom left,' +
            'hsl(from #0cabf3 calc(h + 0) calc(s + 0) calc(l + 0) / 0.2) 5%,' +
            'transparent 21%,' +
            'transparent 78%,' +
            'hsl(from #0cabf3 calc(h + 0) calc(s + 0) calc(l + 0) / 0.3) 100% 100%)',
        backgroundColor: 'hsl(from #0e151d calc(h + 0) calc(s + 0) calc(l + 0) / 0.2)',
        '&:hover': {
            borderColor: 'hsl(from #0cabf3 calc(h + 0) calc(s + 0) calc(l + 0) / 0.8)',
            boxShadow: '0px 0px 40px 30px hsl(from #0cabf3 calc(h + 0) calc(s + 0) calc(l + 0) / 0.3)',
        },
        '&:hover svg': LinkArrowHoverStyle,
        '& h1': {
            backgroundImage: 'linear-gradient(27deg,#2353a5 9%,#0cabf3 33%,#0fa0e9 70%,#2353a5 85%)',
            backgroundClip: 'text',
            color: 'transparent',
        },
    },
    headerText: {
        borderBottom: '1px solid hsl(from #ffffff calc(h + 0) calc(s + 0) calc(l + 0) / 0.1)',
        paddingBottom: '1rem',
    },
    button: {
        borderRadius: '3rem',
        border: '1px solid white',
        transition: 'color,borderColor 0.2s,0.2s',
        padding: '0.625rem 2.5rem',
        fontSize: '1.125rem',
        alignSelf: 'center',
        '& svg':  {
            '--link-arrow-fill': 'white',
            '--link-arrow-stroke': '#071526',
            transition: 'all 0.2s',
            margin: '0 0.25rem -0.125rem 0',
        },
        '& svg circle':  {
            transition: 'stroke,fill 0.2s,0.2s',
        },
        '& svg path':  {
            transition: 'stroke,fill 0.2s,0.2s',
        },
        '&:hover': {
            color: 'rgb(12, 171, 243)',
            borderColor: 'rgb(12, 171, 243)',
        },
        '&:hover svg': {
            '--link-arrow-fill': 'rgb(12, 171, 243)',
            '--link-arrow-stroke': '#071526',
        },
    },
    background: {
        zIndex: -1,
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        overflow: 'hidden',
        '& video': {
            position: 'absolute',
            left: '50%',
            top: 0,
            transform: 'translate(-50%, 0)',
            minWidth: '150%',
            minHeight: '150%',
        },
    },
});


type CommonSignInPageProps = SignInPageProps & {
    ErrorComponent?: ComponentType<{ error?: Error }>;
};

type SingleSignInPageProps = CommonSignInPageProps & {
    provider: SignInProviderConfig;
    auto?: boolean;
};

export const SignInPage = ({
    provider,
    auto,
    onSignInSuccess,
    ErrorComponent,
}: SingleSignInPageProps) => {
    const classes = useStyles();
    const authApi = useApi(provider.apiRef);
    const analytics = useAnalytics();
    const {t} = useTranslationRef(pagesTranslationRef);
    const [error, setError] = useState<Error>();
    const [showLoginPage, setShowLoginPage] = useState<boolean>(false);
    const [searchParams, _setSearchParams] = useSearchParams();
    const errorParam = searchParams.get('error');
    type LoginOpts = { checkExisting?: boolean; showPopup?: boolean };
    const login = async ({ checkExisting, showPopup }: LoginOpts) => {
        try {
            let identityResponse: BackstageIdentityResponse | undefined;
            if (checkExisting) {
                // @ts-ignore
                identityResponse = await authApi.getBackstageIdentity({
                    optional: true,
                });
            }
            if (!identityResponse && (showPopup || auto) && !errorParam) {
                setShowLoginPage(true);
                // @ts-ignore
                identityResponse = await authApi.getBackstageIdentity({
                    instantPopup: true,
                });
                if (!identityResponse) {
                    throw new Error(
                        `The ${provider.title} provider is not configured to support sign-in`,
                    );
                }
            }

            if (!identityResponse) {
                setShowLoginPage(true);
                return;
            }

            // @ts-ignore
            const profile = await authApi.getProfile();
            onSignInSuccess(
                UserIdentity.create({
                    identity: identityResponse.identity,
                    // @ts-ignore
                    authApi,
                    profile,
                }),
            );
            analytics.captureEvent('signIn', 'success');
        } catch (err: any) {
            setError(err);
            setShowLoginPage(true);
        }
    };

    useMountEffect(() => {
        if (errorParam) {
            setError(new Error(errorParam));
        }
        login({ checkExisting: true });
    });

    return showLoginPage ? (
        <main className={classes.main}>
            <div className={classes.background}>
                <video autoPlay muted loop>
                    <source src="signin-background.mp4" type="video/mp4" />
                </video>
            </div>
            <div className={classes.logo}><CaiseLogo /></div>
            <button type='button' className={classes.signin} onClick={() => {login({ showPopup: true });}}>
                <h1>{t('pages.home.title')}</h1>
                <div className={classes.headerText}>{t('pages.home.headerContent')}</div>
                {/*<Typography variant="body1">{provider.message}</Typography>*/}
                {error && error.name !== 'PopupRejectedError' && (
                    ErrorComponent ? (
                        <ErrorComponent error={error} />
                    ) : (
                        <Typography variant="body1" color="error">{error.message}</Typography>
                    )
                )}
                <div className={classes.button}>
                    <LinkArrow fill='var(--link-arrow-fill)' stroke='var(--link-arrow-stroke)'/>
                    {t('pages.signIn')}
                </div>
            </button>
        </main>
    ) : (
        <Progress />
    );
};
